import React, { useState } from 'react';

import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { IconChevronDown } from '@pkg/assets/icons';
import { Portal, PortalArea } from '@pkg/components';
import { useExpireTimestamp } from '@pkg/hooks';

import { Menu, type MenuProps } from './parts';

import {
    DateColumn,
    DateLabel,
    ExpirePopover,
    IconWrapper,
    ReportColumn,
    ReportLabel,
    ShorthandBadge,
    ShorthandBadgeLabel,
    ShorthandGroup,
    ShorthandLabel,
    StyledExpireSelector,
} from './ExpireSelector.style';
import { tokens } from './ExpireSelector.tokens';

export interface ExpireSelectorProps extends MenuProps {
    disabled?: boolean;
}

export function ExpireSelector({ selected = 'week', disabled = false, onSelect }: ExpireSelectorProps) {
    const [translate] = useTranslation('marketplace');
    const [open, onOpenChange] = useState(false);

    const { expireLabel, expireShorthand } = useExpireTimestamp(selected, {
        hour: translate('common-expire-option-1'),
        day: translate('common-expire-option-2'),
        halfWeek: translate('common-expire-option-3'),
        week: translate('common-expire-option-4'),
        month: translate('common-expire-option-5'),
    });

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange,
        placement: 'bottom-end',
        middleware: [offset(tokens.popover.offset), flip({ fallbackAxisSideDirection: 'end' }), shift()],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const handleSelect: ExpireSelectorProps['onSelect'] = (option, time) => {
        onOpenChange(false);
        onSelect(option, time);
    };

    const dropdownMarkup = open ? (
        <Portal destination={PortalArea.Transition}>
            <FloatingFocusManager context={context} modal={false}>
                <ExpirePopover ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                    <Menu selected={selected} onSelect={handleSelect} />
                </ExpirePopover>
            </FloatingFocusManager>
        </Portal>
    ) : null;

    const shorthandBadgeMarkup =
        selected === 'week' ? (
            <ShorthandBadge>
                <ShorthandBadgeLabel>{translate('common-expire-selector-shorthand-badge-label')}</ShorthandBadgeLabel>
            </ShorthandBadge>
        ) : null;

    return (
        <StyledExpireSelector
            ref={refs.setReference}
            aria-label={translate('common-expire-selector-a11y')}
            disabled={disabled}
            {...getReferenceProps()}
        >
            <ReportColumn>
                <ReportLabel>{translate('common-expire-selector-report-label')}</ReportLabel>

                <ShorthandGroup>
                    <ShorthandLabel>{expireShorthand}</ShorthandLabel>
                    {shorthandBadgeMarkup}
                </ShorthandGroup>
            </ReportColumn>

            <DateColumn>
                <DateLabel>{expireLabel}</DateLabel>

                <IconWrapper>
                    <IconChevronDown />
                </IconWrapper>
            </DateColumn>

            <AnimatePresence>{dropdownMarkup}</AnimatePresence>
        </StyledExpireSelector>
    );
}
